/**
 * CannedResponsePicker — S2 Task 8 (spec 2026-07-16 §3.1/§4, veredito visual TL).
 *
 * Painel de busca/seleção de Respostas Prontas aberto pelo atalho "/" (draft
 * vazio) ou pelo botão ⚡ do Composer (modo navegação, `query=''`). `onSelect`
 * NUNCA envia — alimenta a fila de revisão do atendente (Task 9 consome via
 * `stagedResponse` no Composer).
 *
 * Fetch único sem params (`useCannedResponses(true)` — mesma queryKey do
 * `CannedChipsRow`, React Query reusa o cache) + filtro client-side
 * instantâneo por título/categoria/shortcut/conteúdo dos items de texto
 * (case-insensitive). O GET já filtra `status: 'active'` no Engine
 * (`canned-responses.service.ts` `list()` — verificado na fonte), então não
 * há filtro de status redundante aqui.
 *
 * Grupos: 1) vinculadas ao playbook ativo da conversa (topo, "DO PLAYBOOK
 * {name}"); 2) demais ("BIBLIOTECA"). `useMediaAssets` alimenta o resumo
 * honesto dos items (`summarizeCannedResponseItems`) — em 403/indisponível
 * degrada para o resumo sem detalhe de tipo de mídia (mapa vazio), nunca
 * quebra.
 *
 * 403 no fetch principal (cap opt-in negada, server-authoritative — B1
 * residual) → `null`, silencioso, mesma doutrina de `getCannedResponses`.
 *
 * Teclado: o foco permanece na textarea do Composer enquanto o atendente
 * digita "/…" — por isso o listener é em `document` (mirror do padrão já
 * usado em `ConversationHeader` para ⌘/), não no próprio painel. ↑↓ move a
 * seleção sobre a lista achatada (vinculadas primeiro), Enter seleciona,
 * Esc fecha. `preventDefault` nesses keydowns cancela o efeito padrão na
 * textarea (mover cursor / quebrar linha) mesmo disparado a partir de
 * `document` — o cancelamento vale para toda a fase de despacho do evento.
 */
import {
  cannedFirstTextExcerpt,
  matchesCannedQuery,
  partitionCannedByPlaybook,
  summarizeCannedResponseItems,
} from '@lume/shared/canned-responses';
import { useEffect, useMemo, useState } from 'react';

import { useCannedResponses, useMediaAssets } from '../../../../hooks/use-canned-responses';
import { ApiError, type CannedResponseWire, type MediaAssetWire } from '../../../../lib/api-client';

import type { JSX } from 'react';

export interface CannedPickerProps {
  /** Texto após o "/" digitado (sem a barra) ou '' (modo navegação via botão). */
  query: string;
  playbookId: string | null;
  playbookName: string | null;
  /** NUNCA envia — alimenta a fila de revisão (Task 9). */
  onSelect: (response: CannedResponseWire) => void;
  onClose: () => void;
  /**
   * Important #2 pós-review (ARIA combobox) — reporta o `id` DOM da option
   * ativa (↑↓) para o host (Composer) espelhar em `aria-activedescendant`
   * do textarea; `null` quando não há option ativa (lista vazia).
   */
  onActiveDescendantChange?: (id: string | null) => void;
}

/** id DOM estável por option — alvo do `aria-activedescendant` do host. */
function optionDomId(responseId: string): string {
  return `canned-opt-${responseId}`;
}

export function CannedResponsePicker({
  query,
  playbookId,
  playbookName,
  onSelect,
  onClose,
  onActiveDescendantChange,
}: CannedPickerProps): JSX.Element | null {
  const listQ = useCannedResponses(true);
  const mediaQ = useMediaAssets(true);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const mediaById = useMemo(
    () => new Map<string, MediaAssetWire>((mediaQ.data?.data ?? []).map((m) => [m.id, m])),
    [mediaQ.data],
  );

  const trimmedQuery = query.trim();
  const { linked, library } = useMemo(() => {
    const all = listQ.data?.data ?? [];
    const filtered = all.filter((r) => matchesCannedQuery(r, trimmedQuery));
    return partitionCannedByPlaybook(filtered, playbookId);
  }, [listQ.data, trimmedQuery, playbookId]);

  const flat = useMemo(() => [...linked, ...library], [linked, library]);

  // Nova busca/nova sessão → seleção volta ao topo (item mais relevante).
  useEffect(() => {
    setSelectedIndex(0);
  }, [trimmedQuery, flat.length]);

  // Important #2 pós-review (ARIA) — reporta ao host o `id` DOM da option
  // ativa sempre que a seleção/lista muda; `null` sem option (lista vazia).
  const activeResponse = flat[selectedIndex];
  const activeId = activeResponse !== undefined ? optionDomId(activeResponse.id) : null;
  useEffect(() => {
    onActiveDescendantChange?.(activeId);
  }, [activeId, onActiveDescendantChange]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (flat.length === 0 ? 0 : Math.min(i + 1, flat.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selected = flat[selectedIndex];
        if (selected !== undefined) onSelect(selected);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [flat, selectedIndex, onSelect, onClose]);

  // 403 server-authoritative (cap opt-in negada) — silencioso, mesma
  // doutrina do gate-and-split da biblioteca (B1 residual).
  if (listQ.isError && listQ.error instanceof ApiError && listQ.error.status === 403) {
    return null;
  }

  return (
    <div
      className="absolute bottom-full left-0 z-20 mb-1 flex max-h-80 w-full flex-col overflow-hidden rounded-lg border"
      style={{
        borderColor: 'var(--border-strong)',
        background: 'var(--bg-surface)',
        boxShadow: 'var(--shadow-md, 0 4px 16px rgba(0,0,0,0.18))',
      }}
      role="listbox"
      id="canned-picker-listbox"
      aria-label="Respostas prontas"
      data-testid="canned-response-picker"
    >
      <div
        className="flex items-center gap-2 border-b px-3 py-2"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <span className="flex-1 text-sm" style={{ color: 'var(--fg-2)' }}>
          {trimmedQuery !== '' ? `Buscando "${trimmedQuery}"…` : 'Respostas prontas'}
        </span>
        <span className="font-mono text-xs" style={{ color: 'var(--fg-4)' }} aria-hidden>
          /
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {flat.length === 0 ? (
          <p className="px-3 py-4 text-sm" style={{ color: 'var(--fg-3)' }}>
            Nenhuma resposta encontrada.
          </p>
        ) : (
          <>
            {linked.length > 0 && (
              <CannedPickerGroup
                label={`DO PLAYBOOK ${playbookName ?? ''}`.trim()}
                items={linked}
                flat={flat}
                selectedIndex={selectedIndex}
                mediaById={mediaById}
                onSelect={onSelect}
              />
            )}
            {library.length > 0 && (
              <CannedPickerGroup
                label="BIBLIOTECA"
                items={library}
                flat={flat}
                selectedIndex={selectedIndex}
                mediaById={mediaById}
                onSelect={onSelect}
              />
            )}
          </>
        )}
      </div>

      <div
        className="border-t px-3 py-1.5 text-xs"
        style={{ borderColor: 'var(--border-subtle)', color: 'var(--fg-4)' }}
      >
        ↑↓ navegar · Enter revisar antes de enviar · Esc fechar
      </div>
    </div>
  );
}

function CannedPickerGroup({
  label,
  items,
  flat,
  selectedIndex,
  mediaById,
  onSelect,
}: {
  label: string;
  items: CannedResponseWire[];
  flat: CannedResponseWire[];
  selectedIndex: number;
  mediaById: Map<string, MediaAssetWire>;
  onSelect: (response: CannedResponseWire) => void;
}): JSX.Element {
  return (
    <div>
      <div
        className="px-3 pt-2 pb-1 text-2xs font-medium uppercase tracking-wide"
        style={{ color: 'var(--fg-4)' }}
      >
        {label}
      </div>
      {items.map((response) => {
        const isSelected = flat[selectedIndex]?.id === response.id;
        const excerpt = cannedFirstTextExcerpt(response);
        const summary = summarizeCannedResponseItems(response.items, mediaById);
        return (
          <button
            key={response.id}
            type="button"
            role="option"
            id={optionDomId(response.id)}
            aria-selected={isSelected}
            onClick={() => onSelect(response)}
            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left"
            style={
              isSelected
                ? { borderLeft: '2px solid var(--brand)', background: 'var(--brand-subtle)' }
                : { borderLeft: '2px solid transparent' }
            }
          >
            <span className="flex w-full items-center gap-2">
              <span className="flex-1 text-sm font-medium" style={{ color: 'var(--fg-1)' }}>
                {response.title}
              </span>
              {response.category !== null && response.category !== '' && (
                <span
                  className="rounded-full px-2 py-0.5 text-2xs"
                  style={{
                    background: 'var(--bg-surface-2)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--fg-3)',
                  }}
                >
                  {response.category}
                </span>
              )}
              {response.shortcut !== null && response.shortcut !== '' && (
                <span className="font-mono text-2xs" style={{ color: 'var(--fg-4)' }}>
                  /{response.shortcut}
                </span>
              )}
            </span>
            <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
              {excerpt !== null ? `${summary} — ${excerpt}` : summary}
            </span>
          </button>
        );
      })}
    </div>
  );
}
